import { useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  AVAILABLE_TEMPLATE_VARIABLES,
  extractVariablesFromBody,
  buildTemplatePreview,
} from "@/lib/templateVariables";

interface TemplateMessageEditorProps {
  id?: string;
  body: string;
  variables: string[];
  onChange: (body: string, variables: string[]) => void;
}

export function TemplateMessageEditor({
  id = "body",
  body,
  variables,
  onChange,
}: TemplateMessageEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPreview, setShowPreview] = useState(false);

  const applyBody = (nextBody: string) => {
    onChange(nextBody, extractVariablesFromBody(nextBody));
  };

  const insertVariable = (key: string) => {
    const token = `{{${key}}}`;
    const el = textareaRef.current;

    if (!el) {
      applyBody(`${body}${token}`);
      return;
    }

    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const nextBody = body.slice(0, start) + token + body.slice(end);
    applyBody(nextBody);

    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + token.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>
          Mensaje <span className="text-destructive">*</span>
        </Label>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {showPreview ? (
            <EyeOff className="h-3.5 w-3.5" />
          ) : (
            <Eye className="h-3.5 w-3.5" />
          )}
          {showPreview ? "Ocultar vista previa" : "Ver vista previa"}
        </button>
      </div>

      <p className="text-xs text-muted-foreground">
        Haz clic en una variable para insertarla en el mensaje:
      </p>

      <div className="flex flex-wrap gap-1.5 rounded-lg border border-dashed border-border bg-muted/30 p-2">
        {AVAILABLE_TEMPLATE_VARIABLES.map((variable) => {
          const used = variables.includes(variable.key);
          const Icon = variable.icon;
          return (
            <Tooltip key={variable.key}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => insertVariable(variable.key)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                    used
                      ? "border-primary/40 bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-primary",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {variable.label}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px]">
                <p className="font-mono text-xs">{`{{${variable.key}}}`}</p>
                <p className="text-xs text-muted-foreground">
                  {variable.description}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      <Textarea
        id={id}
        ref={textareaRef}
        placeholder="Hola {{nombre}}, tu servicio {{servicio}} vence el {{fecha_vencimiento}}."
        value={body}
        onChange={(e) => applyBody(e.target.value)}
        rows={5}
        className="font-mono text-sm"
      />

      {showPreview && body.trim() && (
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Vista previa
          </p>
          <p className="whitespace-pre-wrap text-sm text-foreground">
            {buildTemplatePreview(body)}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground">
          {variables.length > 0 ? "Variables usadas:" : "Sin variables en el mensaje."}
        </span>
        {variables.map((key) => {
          const known = AVAILABLE_TEMPLATE_VARIABLES.some((v) => v.key === key);
          return (
            <Badge
              key={key}
              variant={known ? "secondary" : "outline"}
              className="font-mono text-[10px]"
            >
              {key}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}
